import { CheckCircleIcon, PlayCircleIcon, LockIcon, ClockIcon, BookOpenIcon, ShieldCheckIcon, InfinityIcon, VideoIcon, FileQuestionIcon, LightbulbIcon, UsersIcon, ClipboardListIcon, FileTextIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Progress } from "@/components/ui/progress"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface IconStatProps {
  icon: React.ReactNode
  count: number | string
  label: string
}

function IconStat({ icon, count, label }: IconStatProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1.5 cursor-help">
          {icon}
          <span>{count}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

export type ModuleStatus = "completed" | "in-progress" | "locked"

export interface AssetCounts {
  Video: number
  Quiz: number
  BrainstormingSession: number
  MeetupSession: number
  Activity: number
  Document: number
}

export interface ModuleCardProps {
  /**
   * Module number
   */
  number: number

  /**
   * Module title
   */
  title: string

  /**
   * Module description
   */
  description: string

  /**
   * Number of chapters
   */
  chapterCount: number

  /**
   * Number of chapters with gated content
   */
  gatedChapterCount?: number

  /**
   * Number of chapters with non-gated content
   */
  nonGatedChapterCount?: number

  /**
   * Per-asset counts (video, quiz, etc.)
   */
  assetCounts?: AssetCounts

  /**
   * Total duration in minutes
   */
  duration: number

  /**
   * Progress percentage (0-100)
   */
  progress: number

  /**
   * Module status
   */
  status: ModuleStatus

  /**
   * Link to module/first incomplete chapter
   */
  href?: string

  /**
   * Click handler
   */
  onClick?: () => void

  /**
   * Additional CSS classes
   */
  className?: string
}

export function ModuleCard({
  number,
  title,
  description,
  chapterCount,
  gatedChapterCount,
  nonGatedChapterCount,
  assetCounts,
  duration,
  progress,
  status,
  href,
  onClick,
  className
}: ModuleCardProps) {
  const isLocked = status === "locked"
  const isCompleted = status === "completed"
  const isInProgress = status === "in-progress"

  const StatusIcon = isCompleted
    ? CheckCircleIcon
    : isInProgress
    ? PlayCircleIcon
    : LockIcon

  const statusColor = isCompleted
    ? "text-green-600 dark:text-green-500"
    : isInProgress
    ? "text-primary"
    : "text-muted-foreground"

  const content = (
    <div
      className={cn(
        "group relative bg-card border border-border rounded-lg p-6 transition-all duration-200",
        !isLocked && "hover:shadow-md hover:border-primary/50 cursor-pointer",
        isLocked && "opacity-60 cursor-not-allowed",
        className
      )}
      onClick={!isLocked ? onClick : undefined}
    >
      {/* Lock Tooltip */}
      {isLocked && (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-background/80 rounded-lg z-10">
          <div className="bg-popover border border-border rounded-md px-4 py-2 shadow-lg">
            <p className="text-sm text-popover-foreground">
              Complete previous module to unlock
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-6">
        {/* Module Number Badge */}
        <div className="flex-shrink-0">
          <div
            className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg",
              isCompleted && "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
              isInProgress && "bg-primary/10 text-primary",
              isLocked && "bg-muted text-muted-foreground"
            )}
          >
            {number}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title and Status Icon */}
          <div className="flex items-start justify-between gap-4 mb-2">
            <h3 className="text-lg font-semibold text-foreground line-clamp-1">
              {title}
            </h3>
            <StatusIcon className={cn("w-6 h-6 flex-shrink-0", statusColor)} />
          </div>

          {/* Description */}
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {description}
          </p>

          {/* Meta Information */}
          <TooltipProvider delayDuration={150}>
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4 flex-wrap">
              <IconStat icon={<BookOpenIcon className="w-4 h-4" />} count={chapterCount} label="Chapters" />
              {gatedChapterCount != null && gatedChapterCount > 0 && (
                <IconStat icon={<ShieldCheckIcon className="w-4 h-4 text-primary" />} count={gatedChapterCount} label="Gated chapters" />
              )}
              {nonGatedChapterCount != null && nonGatedChapterCount > 0 && (
                <IconStat icon={<InfinityIcon className="w-4 h-4 text-blue-600" />} count={nonGatedChapterCount} label="Non-gated chapters" />
              )}
              {assetCounts && assetCounts.Video > 0 && (
                <IconStat icon={<VideoIcon className="w-4 h-4 text-blue-500" />} count={assetCounts.Video} label="Videos" />
              )}
              {assetCounts && assetCounts.Quiz > 0 && (
                <IconStat icon={<FileQuestionIcon className="w-4 h-4 text-purple-500" />} count={assetCounts.Quiz} label="Quizzes" />
              )}
              {assetCounts && assetCounts.BrainstormingSession > 0 && (
                <IconStat icon={<LightbulbIcon className="w-4 h-4 text-amber-500" />} count={assetCounts.BrainstormingSession} label="Brainstorming sessions" />
              )}
              {assetCounts && assetCounts.MeetupSession > 0 && (
                <IconStat icon={<UsersIcon className="w-4 h-4 text-green-500" />} count={assetCounts.MeetupSession} label="Meetups" />
              )}
              {assetCounts && assetCounts.Activity > 0 && (
                <IconStat icon={<ClipboardListIcon className="w-4 h-4 text-emerald-500" />} count={assetCounts.Activity} label="Validation" />
              )}
              {assetCounts && assetCounts.Document > 0 && (
                <IconStat icon={<FileTextIcon className="w-4 h-4 text-orange-500" />} count={assetCounts.Document} label="Documents" />
              )}
              <IconStat icon={<ClockIcon className="w-4 h-4" />} count={`${duration} min`} label="Estimated duration" />
            </div>
          </TooltipProvider>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium text-foreground">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </div>
    </div>
  )

  if (href && !isLocked) {
    return (
      <a href={href} className="block">
        {content}
      </a>
    )
  }

  return content
}
